import { createFetch, ofetch } from 'ofetch';

async function wait(duration) {
  return new Promise((res) => {
    setTimeout(() => {
      res(true);
    }, duration);
  });
}

function messageListFromString(input) {
  const messages = [];
  let line = "";
  let ignoreNextNewline = false;
  let data;
  let id;
  let event;
  let retry;
  let previousChar;
  let pendingIndex = 0;
  let isEndOfMessage = false;
  function handleParseLine(pIndex) {
    const result = parseLine(line);
    data = result.data ?? data;
    id = result.id ?? id;
    event = result.event ?? event;
    retry = result.retry ?? retry;
    if (isEndOfMessage) {
      if (typeof data === "string") {
        messages.push({
          id,
          data,
          event: event ?? "message",
          retry
        });
      }
      id = void 0;
      data = void 0;
      event = void 0;
      retry = void 0;
      pendingIndex = pIndex;
    }
    line = "";
  }
  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    switch (char) {
      case "\r": {
        isEndOfMessage = previousChar === "\n" || previousChar === "\r";
        ignoreNextNewline = true;
        const pIndex = input[i + 1] === "\n" ? i + 2 : i + 1;
        handleParseLine(pIndex);
        break;
      }
      case "\n": {
        if (ignoreNextNewline) {
          ignoreNextNewline = false;
          break;
        }
        isEndOfMessage = previousChar === "\n";
        handleParseLine(i + 1);
        break;
      }
      default:
        line += char;
        break;
    }
    previousChar = char;
  }
  return {
    messages,
    leftoverData: input.substring(pendingIndex)
  };
}
function parseLine(input) {
  if (input.startsWith("data:")) {
    return { data: input.substring(5).trim() };
  }
  if (input.startsWith("id:")) {
    return { id: input.substring(3).trim() };
  }
  if (input.startsWith("event:")) {
    return {
      event: input.substring(6).trim()
    };
  }
  if (input.startsWith("retry:")) {
    const val = Number(input.substring(6).trim());
    if (!Number.isNaN(val)) {
      if (Number.isInteger(val)) {
        return { retry: val };
      } else {
        return { retry: Math.round(val) };
      }
    }
  }
  return {};
}
async function getBytes(controller, stream, onChunk) {
  const reader = stream.getReader();
  let result;
  while (!controller.signal.aborted && !(result = await reader.read()).done) {
    onChunk(result.value);
  }
}

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const EventStreamContentType = "text/event-stream";
const LastEventIdHeader = "last-event-id";
class EventSourcePlus {
  constructor(url, options = {
    method: "get",
    headers: {}
  }) {
    __publicField(this, "url");
    __publicField(this, "lastEventId");
    __publicField(this, "options");
    __publicField(this, "retryCount", 0);
    __publicField(this, "retryInterval", 0);
    __publicField(this, "maxRetryCount");
    __publicField(this, "maxRetryInterval");
    __publicField(this, "fetch");
    this.url = url;
    this.options = options;
    this.maxRetryCount = options.maxRetryCount;
    this.maxRetryInterval = options.maxRetryInterval ?? 3e4;
    this.fetch = createFetch({ fetch: options.fetch }) ?? ofetch;
  }
  async _handleRetry(controller, hooks) {
    this.retryCount++;
    if (typeof this.maxRetryCount === "number" && this.retryCount >= this.maxRetryCount) {
      return;
    }
    if (this.retryInterval === 0) {
      this.retryInterval = 2;
    } else if (this.retryInterval > 0) {
      this.retryInterval = this.retryInterval * 2;
      if (this.retryInterval >= this.maxRetryInterval) {
        this.retryInterval = this.maxRetryInterval;
      }
    }
    await wait(this.retryInterval);
    controller._abortController.abort();
    controller._abortController = new AbortController();
    await this._handleConnection(controller, hooks);
  }
  async _handleConnection(controller, hooks) {
    let headers = {};
    if (typeof this.options.headers === "function") {
      const result = this.options.headers();
      if ("then" in result && typeof result.then === "function") {
        headers = await result.then((data) => data);
      } else {
        headers = result;
      }
    } else {
      headers = this.options.headers ?? {};
    }
    if (typeof headers.accept !== "string") {
      headers.accept = EventStreamContentType;
    }
    if (typeof this.lastEventId === "string" && typeof headers[LastEventIdHeader] !== "string") {
      headers[LastEventIdHeader] = this.lastEventId;
    }
    const finalOptions = {
      ...this.options,
      method: this.options.method ?? "get",
      responseType: "stream",
      headers,
      signal: controller.signal,
      retry: false,
      onRequest: hooks.onRequest,
      onRequestError: hooks.onRequestError,
      onResponse: async (context) => _handleResponse(context, hooks),
      onResponseError: async (context) => {
        if (typeof hooks.onResponseError === "function") {
          await hooks.onResponseError(
            context
          );
        }
        if (context.error instanceof Error) {
          throw context.error;
        }
        throw new Error(
          `Received { STATUS_CODE: ${context.response.status} STATUS_TEXT: ${context.response.statusText} }`
        );
      }
    };
    try {
      const result = await this.fetch(this.url, finalOptions);
      this.retryCount = 0;
      this.retryInterval = 0;
      const decoder = new TextDecoder();
      let pendingData = "";
      await getBytes(controller, result, (arr) => {
        const text = pendingData + decoder.decode(arr);
        const result2 = messageListFromString(text);
        pendingData = result2.leftoverData ?? "";
        for (const message of result2.messages) {
          if (typeof message.id === "string" && message.id.length > 0) {
            this.lastEventId = message.id;
          }
          hooks.onMessage(message);
        }
      });
    } catch (_) {
      if (controller.signal.aborted) {
        return;
      }
      return this._handleRetry(controller, hooks);
    }
    if (controller.signal.aborted) {
      return;
    }
    return this._handleRetry(controller, hooks);
  }
  listen(options) {
    const controller = new EventSourceController(new AbortController());
    void this._handleConnection(controller, options);
    return controller;
  }
}
class EventSourceController {
  constructor(controller) {
    /**
     * Do not modify. For internal use.
     */
    __publicField(this, "_abortController");
    this._abortController = controller ?? new AbortController();
  }
  abort(reason) {
    this._abortController.abort(reason);
  }
  get signal() {
    return this._abortController.signal;
  }
}
const HTTP_METHOD_VALS = [
  "get",
  "head",
  "post",
  "put",
  "delete",
  "connect",
  "options",
  "trace",
  "patch"
];
async function _handleResponse(context, hooks) {
  if (typeof hooks.onResponse === "function") {
    await hooks.onResponse(context);
  }
  const contentType = context.response.headers.get("Content-Type");
  if (typeof contentType !== "string" || !contentType.includes(EventStreamContentType)) {
    const error = new Error(
      `Expected server to response with Content-Type: '${EventStreamContentType}'. Got '${contentType}'`
    );
    context.error = error;
    if (typeof hooks.onResponseError === "function") {
      hooks.onResponseError(context);
    }
    throw error;
  }
}

export { EventSourceController, EventSourcePlus, EventStreamContentType, HTTP_METHOD_VALS, _handleResponse, getBytes, messageListFromString, parseLine };
